import { Activity, TrendingUp, Clock } from 'lucide-react';

/**
 * "Today's Market Pulse" component for mobile
 * Shows high-level market intelligence summary
 */
const MarketPulse = ({ anomalies, lastScanAt, isMarketOpen }) => {
  if (!anomalies || anomalies.length === 0) return null;
  
  // Count high-probability anomalies (score >= 50)
  const highProbCount = anomalies.filter(a => (a.unusual_activity_score || 0) >= 50).length;
  
  // Get time since last scan
  const getTimeSinceScan = () => {
    if (!lastScanAt) return 'Unknown';
    const lastScan = new Date(lastScanAt);
    const now = new Date();
    const diffMins = Math.floor((now - lastScan) / (1000 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    const hours = Math.floor(diffMins / 60);
    return `${hours}h ago`;
  };
  
  // Calculate market sentiment
  const upMoves = anomalies.filter(a => a.daily_return >= 0).length;
  const downMoves = anomalies.length - upMoves;
  const sentiment = upMoves > downMoves ? 'bullish' : upMoves < downMoves ? 'bearish' : 'neutral';
  
  // Generate AI summary
  const getAISummary = () => {
    const maxScore = Math.max(...anomalies.map(a => a.unusual_activity_score || 0));
    const topStock = anomalies.find(a => a.unusual_activity_score === maxScore);
    
    if (sentiment === 'bullish' && highProbCount > 3) {
      return `Strong bullish signals detected. ${topStock?.symbol || 'Multiple stocks'} showing elevated activity.`;
    }
    if (sentiment === 'bearish' && highProbCount > 3) {
      return `Elevated selling pressure detected. Monitor ${topStock?.symbol || 'key positions'} closely.`;
    }
    if (highProbCount > 0) {
      return `${highProbCount} high-conviction opportunities identified. ${topStock?.symbol} leads with ${maxScore.toFixed(0)}/100 signal strength.`;
    }
    return 'Market activity within normal ranges. Monitoring for emerging patterns.';
  };
  
  return (
    <div 
      className="rounded-xl p-4 mb-4"
      style={{ 
        backgroundColor: 'rgba(59, 130, 246, 0.05)',
        border: '1px solid rgba(59, 130, 246, 0.15)'
      }}
      data-testid="market-pulse"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)' }}
          >
            <Activity className="h-4 w-4" style={{ color: '#60A5FA' }} />
          </div>
          <div>
            <h2 className="text-sm font-semibold" style={{ color: '#F8FAFC' }}>
              Today's Market Pulse
            </h2>
            <div className="flex items-center gap-1.5 mt-0.5">
              <Clock className="h-3 w-3" style={{ color: '#64748B' }} />
              <span className="text-xs" style={{ color: '#64748B' }}>
                Last scan: {getTimeSinceScan()}
              </span>
            </div>
          </div>
        </div>
        
        {/* Market status */}
        <div 
          className="flex items-center gap-1.5 px-2 py-1 rounded-md"
          style={{ 
            backgroundColor: isMarketOpen ? 'rgba(34, 197, 94, 0.1)' : 'rgba(251, 191, 36, 0.1)'
          }}
        >
          <div 
            className="w-2 h-2 rounded-full"
            style={{ 
              backgroundColor: isMarketOpen ? '#22C55E' : '#fbbf24',
              animation: isMarketOpen ? 'pulse-glow 2s ease-in-out infinite' : 'none'
            }}
          />
          <span 
            className="text-xs font-medium"
            style={{ color: isMarketOpen ? '#4ade80' : '#fbbf24' }}
          >
            {isMarketOpen ? 'Live' : 'Closed'}
          </span>
        </div>
      </div>
      
      {/* High-probability count */}
      <div 
        className="flex items-center gap-2 p-3 rounded-lg mb-3"
        style={{ backgroundColor: 'rgba(248, 113, 113, 0.08)' }}
      >
        <span className="text-2xl">🔥</span>
        <div>
          <span className="text-lg font-bold" style={{ color: '#F8FAFC' }}>
            {highProbCount}
          </span>
          <span className="text-sm ml-1.5" style={{ color: '#94A3B8' }}>
            High-Probability Anomalies Detected
          </span>
        </div>
      </div>
      
      {/* AI Summary */}
      <p className="text-sm leading-relaxed" style={{ color: '#CBD5E1' }}>
        {getAISummary()}
      </p>
      
      {/* Quick stats */}
      <div className="flex items-center gap-4 mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center gap-1.5">
          <TrendingUp className="h-3.5 w-3.5" style={{ color: '#4ade80' }} />
          <span className="text-xs" style={{ color: '#94A3B8' }}>
            {upMoves} up
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <TrendingUp className="h-3.5 w-3.5 rotate-180" style={{ color: '#f87171' }} />
          <span className="text-xs" style={{ color: '#94A3B8' }}>
            {downMoves} down
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Activity className="h-3.5 w-3.5" style={{ color: '#60A5FA' }} />
          <span className="text-xs" style={{ color: '#94A3B8' }}>
            {anomalies.length} total
          </span>
        </div>
      </div>
      
      <style>{`
        @keyframes pulse-glow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default MarketPulse;
